import { ApiError } from "@airange/shared/api";
import { readRequiredAuthMemberRoleArray } from "@airange/shared/auth-member-roles";
import { eduAdminOpenApiClient } from "../generated/client";
import type { operations } from "@airange/generated-contracts/edu";
import { unwrapOpenApiResult } from "./openapi-result";

type OperationResponse<
  TOperation extends { responses: Record<number, unknown> },
  TStatus extends keyof TOperation["responses"],
> = TOperation["responses"][TStatus] extends {
  content: { "application/json": infer TResponse };
}
  ? TResponse
  : never;

type GetHealthOperation = operations["health_check_api_health_get"];
type GetMeOperation = operations["get_auth_me_api_auth_me_get"];
type GetAdminOverviewOperation = operations["get_admin_overview_api_overview_admin_get"];

export type EduHealth = OperationResponse<GetHealthOperation, 200>;
export type EduCurrentUser = OperationResponse<GetMeOperation, 200>;
export type EduCurrentUserRoleSource = Pick<EduCurrentUser, "application_roles">;
export type EduCurrentUserRoleKey = EduCurrentUser["application_roles"][number];
export type EduAdminOverview = OperationResponse<GetAdminOverviewOperation, 200>;
export type EduAdminOverviewQuickLink = EduAdminOverview["center_entries"][number];
export type EduAdminOverviewScopeStat = EduAdminOverview["scope_stats"][number];
export type EduAdminOverviewThemeSection = EduAdminOverview["theme_sections"][number];
export type EduAdminOverviewThemeItem = EduAdminOverviewThemeSection["items"][number];

export type EduPaginationQuery = {
  page?: number;
  pageSize?: number;
};

export type EduAuditListQuery = EduPaginationQuery & {
  created_by_keyword?: string;
  updated_by_keyword?: string;
  created_from?: string;
  created_to?: string;
  updated_from?: string;
  updated_to?: string;
};

export type EduAuditFields = {
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
};

const EDU_CURRENT_USER_ROLE_KEYS = [
  "edu_admin",
  "edu_manager",
  "edu_teacher",
  "edu_assistant",
  "edu_student",
] as const satisfies readonly EduCurrentUserRoleKey[];

export function readEduCurrentUserApplicationRoles(
  currentUser: EduCurrentUserRoleSource,
  path = "current_user.application_roles",
): EduCurrentUserRoleKey[] {
  return readRequiredAuthMemberRoleArray(currentUser, {
    allowedRoles: EDU_CURRENT_USER_ROLE_KEYS,
    path,
    createError: (issue) =>
      new ApiError(500, {
        code: "edu.auth.data_contract_invalid",
        message: issue.message,
      }),
  });
}

const eduCommonApi = {
  async getHealth(): Promise<EduHealth> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.GET("/api/health"));
  },
  async getMe(): Promise<EduCurrentUser> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.GET("/api/auth/me"));
  },
  async getAdminOverview(): Promise<EduAdminOverview> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.GET("/api/overview/admin"));
  },
};

export default eduCommonApi;
